<template>
  <div class="columns-config-wrapper">
    <a-input
      v-model:value="searchText"
      :placeholder="$t('搜索列名/字段名')"
      allow-clear
      style="margin-bottom: 8px"
    />
    <div class="table-wrapper" ref="tableWrapperRef">
      <s-table
        :columns="columnsConfig"
        :rowHeight="30"
        :data-source="filteredDataSource"
        :row-selection="rowSelection"
        v-model:selectedRowKeys="selectedRowKeys"
        expand-row-by-click
        @row-drag-end="onRowDragEnd"
        :row-key="'key'"
        :pagination="false"
        :height="tableHeight"
      >
        <template #bodyCell="{ column, index, text, record }">
          <template v-if="column.dataIndex == 'fixed'">
            <a-radio-group
              v-if="!record?.is_children"
              v-model:value="record.fixed"
              size="small"
              @change="change_fixed"
            >
              <a-radio-button value="left">{{ $t('左') }}</a-radio-button>
              <a-radio-button :value="undefined">{{ $t('不固定') }}</a-radio-button>
              <a-radio-button value="right">{{ $t('右') }}</a-radio-button>
            </a-radio-group>
          </template>
          <template v-if="column.dataIndex == 'autoHeight'">
            <a-switch
              v-if="!(record.children && record.children.length > 0)"
              v-model:checked="record.autoHeight"
              :checked-value="true"
              :un-checked-value="false"
              :checked-children="$t('自动')"
              :un-checked-children="$t('固定')"
              @change="change_autoHeight"
            />
          </template>
          <template v-if="column.dataIndex == 'autoWidth'">
            <a-switch
              v-if="!(record.children && record.children.length > 0) && record?.resizable"
              v-model:checked="record.autoWidth"
              :checked-value="true"
              :un-checked-value="false"
              :checked-children="$t('自动')"
              :un-checked-children="$t('固定')"
              @click="change_autoWidth"
            />
          </template>
          <template v-if="column.dataIndex == 'ellipsis'">
            <a-switch
              v-if="!(record.children && record.children.length > 0) && record?.resizable"
              v-model:checked="record.ellipsis"
              :checked-value="true"
              :un-checked-value="false"
              :checked-children="$t('开启')"
              :un-checked-children="$t('关闭')"
              @click="change_ellipsis(record)"
            />
          </template>
        </template>
      </s-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { Column } from '../typing';

export default defineComponent({
  name: 'ColumnsConfig',
  props: {
    columns: {
      type: Array as () => Array<Column>,
      default: () => [],
    },
  },
  emits: ['change'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const searchText = ref('');
    const tableWrapperRef = ref<HTMLElement | null>(null);
    const tableHeight = ref(500);

    // 监听 table-wrapper 高度，让 s-table 自动占满容器
    useResizeObserver(tableWrapperRef, entries => {
      const height = entries[0].contentRect.height;
      if (height > 0) {
        tableHeight.value = height;
      }
    });

    const columnsConfig = ref([
      {
        title: t('列名称'),
        dataIndex: 'title',
        key: 'title',
        rowDrag: true,
        width: 270,
      },
      {
        title: t('冻结列'),
        dataIndex: 'fixed',
        key: 'fixed',
        width: 200,
      },
      {
        title: t('行高'),
        dataIndex: 'autoHeight',
        width: 100,
        key: 'autoHeight',
      },
      {
        title: t('列宽'),
        dataIndex: 'autoWidth',
        width: 100,
        key: 'autoWidth',
      },
      {
        title: t('超宽省略'),
        dataIndex: 'ellipsis',
        width: 100,
        key: 'ellipsis',
      },
    ]);

    const dataSource = computed(() => {
      return props.columns.filter(item => item.key !== 'index' && item.key !== 'action');
    });

    const filteredDataSource = computed(() => {
      if (!searchText.value) return dataSource.value;
      const search = searchText.value.toLowerCase();

      const filterNode = (nodes: any[]) => {
        return nodes.reduce((acc: any[], node: any) => {
          const matchSelf =
            node.title?.toLowerCase().includes(search) ||
            node.dataIndex?.toLowerCase().includes(search) ||
            node.key?.toLowerCase().includes(search);

          if (node.children && node.children.length > 0) {
            const filteredChildren = filterNode(node.children);
            if (filteredChildren.length > 0) {
              acc.push({ ...node, children: filteredChildren });
            } else if (matchSelf) {
              acc.push({ ...node, children: [] });
            }
          } else if (matchSelf) {
            acc.push(node);
          }
          return acc;
        }, []);
      };

      return filterNode(dataSource.value);
    });

    const getAllKeysChecked = (children: any) => {
      let allChildrenChecked = true;
      children.forEach((item: any) => {
        if (item.children && item.children.length > 0) {
          allChildrenChecked = allChildrenChecked && getAllKeysChecked(item.children);
        } else {
          allChildrenChecked = allChildrenChecked && item?.checked !== false;
        }
      });
      return allChildrenChecked;
    };

    const add_selectedRowKeys = (record: any) => {
      let list = [];
      record.children.forEach((item: any) => {
        if (item.children && item.children.length > 0) {
          const allChildrenChecked = getAllKeysChecked(item.children);
          if (allChildrenChecked) {
            list.push(item.key);
          }
          list = list.concat(add_selectedRowKeys(item));
        } else {
          if (item?.checked !== false) {
            list.push(item.key);
          }
        }
      });
      return list;
    };

    const selectedRowKeys = computed(() => {
      if (props.columns) {
        let list = [];
        props.columns.forEach((item: any) => {
          if (item.children && item.children.length > 0) {
            const allChildrenChecked = getAllKeysChecked(item.children);
            if (allChildrenChecked) {
              list.push(item.key);
            }
            list = list.concat(add_selectedRowKeys(item));
          } else {
            if (item?.checked) {
              list.push(item.key);
            }
          }
        });
        return list;
      } else {
        return [];
      }
    });

    const rowSelection = ref({
      checkStrictly: false,
      hideSelectAll: true,
      onSelect: (record: any, selected: boolean) => {
        const change_selecte_children_props = (children: any) => {
          children.forEach((item: any) => {
            if (item.children && item.children.length > 0) {
              change_selecte_children_props(item.children);
            }
            item.checked = selected;
          });
        };

        const change_selecte_props = (obj: any, key: any, is_checked: any) => {
          obj.children.forEach((item: any) => {
            if (item.key == key) {
              if (is_checked && item.children && item.children.length > 0) {
                change_selecte_children_props(item.children);
              }
              if (selected) {
                item.checked = true;
                obj.checked = true;
                if (obj.is_children) {
                  change_selecte_dataSource(obj.key, false);
                }
              } else {
                item.checked = false;
              }
            }
            if (item.children && item.children.length > 0) {
              change_selecte_props(item, key, is_checked);
            }
          });
        };

        const change_selecte_dataSource = (key: any, is_checked: any) => {
          dataSource.value.forEach((item: any) => {
            if (item.key == key) {
              if (item.children && item.children.length > 0) {
                change_selecte_children_props(item.children);
              }
              item.checked = selected;
            }
            if (item.children && item.children.length > 0) {
              change_selecte_props(item, key, is_checked);
            }
          });
        };

        change_selecte_dataSource(record.key, true);
        emit('change', dataSource.value);
      },
    });

    const findParent = (data: any[], childKey: any) => {
      let foundParentKey: any;
      const searchParent = (id: any, keys: any[], childKeys: any) => {
        for (const key of keys) {
          if (key.key === childKeys) {
            foundParentKey = id;
            return foundParentKey;
          } else if (key.children && key.children.length > 0) {
            foundParentKey = searchParent(key.key, key.children, childKeys);
            if (foundParentKey !== undefined) return foundParentKey;
          }
        }
        return undefined;
      };
      return searchParent(null, data, childKey);
    };

    const onRowDragEnd = ({ record, preTargetInfo, nextTargetInfo }) => {
      return new Promise((reslove, reject) => {
        // 获取当前拖拽节点的父节点ID
        const currentParentId = findParent(dataSource.value, record.key);

        if (preTargetInfo) {
          // 如果拖拽到某个节点之后，检查该目标节点的父节点是否与当前节点相同
          const targetParentId = findParent(dataSource.value, preTargetInfo.record.key);
          if (currentParentId === targetParentId) {
            reslove(true);
          } else {
            reject();
          }
        } else if (nextTargetInfo) {
          // 如果拖拽到某个节点之前，检查该目标节点的父节点是否与当前节点相同
          const targetParentId = findParent(dataSource.value, nextTargetInfo.record.key);
          if (currentParentId === targetParentId) {
            reslove(true);
          } else {
            reject();
          }
        } else {
          reject();
        }
      }).then(() => {
        setTimeout(() => {
          emit('change', dataSource.value);
        }, 200);
      });
    };

    const change_fixed = () => emit('change', dataSource.value);
    const change_autoHeight = () => emit('change', dataSource.value);
    const change_autoWidth = () => emit('change', dataSource.value);

    const recursion_change_ellipsis = (obj: any, record: any) => {
      obj.children.forEach((item: any) => {
        if (item.key == record.key) {
          item.ellipsis = record.ellipsis;
        }
        if (item.children && item.children.length > 0) {
          recursion_change_ellipsis(item, record);
        }
      });
    };

    const change_ellipsis = (record: any) => {
      const list = dataSource.value;
      list.forEach((item: any) => {
        if (item.key == record.key) {
          item.ellipsis = record.ellipsis;
        }
        if (item.children && item.children.length > 0) {
          recursion_change_ellipsis(item, record);
        }
      });
      emit('change', list);
    };

    return {
      searchText,
      tableWrapperRef,
      tableHeight,
      columnsConfig,
      dataSource,
      filteredDataSource,
      rowSelection,
      selectedRowKeys,
      onRowDragEnd,
      change_fixed,
      change_autoHeight,
      change_autoWidth,
      change_ellipsis,
    };
  },
});
</script>

<style lang="less" scoped>
.columns-config-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
}
.table-wrapper {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
</style>
